import React, { ReactNode, useEffect, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { ListUser } from "../data/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Flashbar,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import title from "process";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import {
  createAdmin,
  deleteAdmin,
  updateAdmin,
} from "../../../../graphql/mutations";
import { fetchAuthSession } from "aws-amplify/auth";
const client = generateClient();

async function checkUserGroup() {
  try {
    const { tokens } = await fetchAuthSession();
    // Normalize user groups to be an array of strings
    const groups = tokens?.accessToken.payload["cognito:groups"];
    if (Array.isArray(groups)) {
      return groups as string[]; // Safe to cast as string[]
    } else if (typeof groups === "string") {
      return [groups]; // Convert a single string to an array
    } else {
      return []; // Default to an empty array if not an array or string
    }
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return []; // Default to an empty array in case of an error
  }
}

async function checkCurrentUser(): Promise<{
  username: string;
  email: string | null;
}> {
  try {
    const { tokens } = await fetchAuthSession();

    // Extract user's information from fetchauthsession
    const currentUser =
      tokens?.idToken?.payload || tokens?.accessToken?.payload;
    if (currentUser) {
      const username =
        typeof currentUser["cognito:username"] === "string"
          ? currentUser["cognito:username"]
          : "";

      const email =
        typeof currentUser.email === "string" ? currentUser.email : null;
      return { username, email };
    } else {
      console.error("No User information found");
      return { username: "", email: null };
    }
  } catch (error) {
    console.error("error fetching current user information :", error);
    return { username: "", email: null };
  }
}

interface FlashbarItem {
  type: "info" | "warning" | "error" | "success"; // Adjust according to your requirements
  dismissible: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  content: React.ReactNode;
  id: string;
}

async function createAdminApi(
  nik: string,
  name: string,
  email: string,
  position: string,
  tenant: string,
  username: string,
  password: string,
  accessrole: string
) {
  try {
    await client.graphql({
      query: createAdmin,
      authMode: "userPool",
      variables: {
        input: {
          nik: nik,
          name: name,
          email: email,
          position: position,
          tenant: tenant,
          username: username,
          password: password,
          accessrole: accessrole,
        },
      },
    });
  } catch (err) {
    console.log(err);
  }
}

async function deleteAdminApi(id: string) {
  try {
    await client.graphql({
      query: deleteAdmin,
      authMode: "userPool",
      variables: {
        input: {
          id,
        },
      },
    });
    // Optionally, refresh the list after deletion
    // fetchOverview();
  } catch (err) {
    console.log("Error deleting user : ", err);
  }
}

async function updateAdminApi(
  id: string,
  nik: string,
  name: string,
  email: string,
  position: string,
  tenant: string,
  username: string,
  password: string,
  accessrole: string
) {
  try {
    await client.graphql({
      query: updateAdmin,
      authMode: "userPool",
      variables: {
        input: {
          id: id,
          nik: nik,
          name: name,
          email: email,
          position: position,
          tenant: tenant,
          username: username,
          password: password,
          accessrole: accessrole,
        },
      },
    });
  } catch (err) {
    console.log("Error updating user: ", err);
  }
}

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  ListUser: ListUser[];
  refreshData: () => void;
}

const columnDefinitions: TableProps<ListUser>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: 70,
  },
  {
    header: "NIK",
    cell: ({ NIK }) => <Link to={`/admin/users/${NIK}`}>{NIK}</Link>,
    sortingField: "NIK",
    width: 100,
  },
  {
    header: "Name",
    cell: ({ Name }) => Name,
    sortingField: "Name",
    maxWidth: 100,
  },
  {
    header: "Email",
    cell: ({ Email }) => Email,
    sortingField: "Email",
    maxWidth: 150,
  },
  {
    header: "Position",
    cell: ({ Position }) => Position,
    sortingField: "Position",
    maxWidth: 100,
  },
  {
    header: "Tenant",
    cell: ({ tenant }) => tenant,
    sortingField: "Tenant",
    maxWidth: 100,
  },
  {
    header: "Username",
    cell: ({ Username }) => Username,
    sortingField: "Position",
    maxWidth: 100,
  },
  {
    header: "Password",
    cell: ({ Password }) => Password,
    sortingField: "Position",
    maxWidth: 100,
  },

  {
    header: "Access Role",
    cell: ({ AccessRole }) => AccessRole,
    sortingField: "Class",
    minWidth: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly ListUser[] = [],
  selectedItems: readonly ListUser[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({
  ListUser,
  refreshData,
}: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 20 });
  const [visible, setVisible] = React.useState(false);
  const [visible2, setVisible2] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  {
    /*const handleCancelClick = () => {
    setVisible(false);
  };*/
  }
  const [isModalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [adminNIK, setadminNIK] = useState("");
  const [adminName, setadminName] = useState("");
  const [adminEmail, setadminEmail] = useState("");
  const [adminPosition, setadminPosition] = useState("");
  const [adminTenant, setadminTenant] = useState("");
  const [adminUsername, setadminUsername] = useState("");
  const [adminPassword, setadminPassword] = useState("");
  const [adminAccessrole, setadminAccessrole] = useState("");
  const [selectedAdminId, setselectedAdminId] = useState<string | null>(null);
  const [itemsFlashbar, setItemsFlashbar] = useState<FlashbarItem[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // for timestamp
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  useEffect(() => {
    async function initialize() {
      const groups = await checkUserGroup();
      const user = await checkCurrentUser();
      setCurrentUser(user.username);
      setCurrentUserEmail(user.email ?? "");
      setUserGroups(groups);
    }
    initialize();
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (selectedAdminId) {
        await updateAdminApi(
          selectedAdminId,
          adminNIK,
          adminName,
          adminEmail,
          adminPosition,
          adminTenant,
          adminUsername,
          adminPassword,
          adminAccessrole
        );
        // for transaction log
        console.log(
          `Admin Transaction Log (Update Item):
        Admin ID : ${selectedAdminId}
        Admin NIK : ${adminNIK}
        User Group : ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}
`
        );
        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "User successfully updated.",
            id: "message_3",
          },
        ]);
      } else {
        await createAdminApi(
          adminNIK,
          adminName,
          adminEmail,
          adminPosition,
          adminTenant,
          adminUsername,
          adminPassword,
          adminAccessrole
        );
        // for transaction log
        console.log(
          `Admin Transaction Log (Create Item):
        Admin NIK : ${adminNIK}
        User Group : ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );
        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "User successfully created.",
            id: "message_2",
          },
        ]);
      }
      refreshData();
      setVisible(false);
      setVisible2(false);
      clearFormState();
    } catch (error) {
      console.error("Error Updating/Creating User:", error);
      setItemsFlashbar([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "An error occurred. Please try again.",
          id: "message_error",
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const selectedItem = collectionProps.selectedItems?.[0];
    if (selectedItem) {
      await deleteAdminApi(selectedItem.id);
      // for transaction log
      console.log(
        `Admin Transaction Log (Delete Item):
        Admin ID : ${selectedAdminId}
        Admin NIK : ${adminNIK}
        User Group : ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
      );
      refreshData();
      setModalDeleteVisible(false);
      setItemsFlashbar([
        {
          type: "warning",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "User successfully deleted !",
          id: "message_1",
        },
      ]);
    }
  };

  const handleDeleteCOnfirmation = () => {
    setModalDeleteVisible(true);
  };

  const clearFormState = () => {
    setadminNIK("");
    setadminName("");
    setadminEmail("");
    setadminPosition("");
    setadminTenant("");
    setadminUsername("");
    setadminPassword("");
    setadminAccessrole("");
    setselectedAdminId(null);
  };

  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<ListUser>(ListUser, {
    filtering: {
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Modal
        onDismiss={() => setModalDeleteVisible(false)}
        visible={isModalDeleteVisible}
        closeAriaLabel='Close modal'
        header='Confirm Delete'
        footer={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => setModalDeleteVisible(false)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleDelete}>
              Confirm
            </Button>
          </SpaceBetween>
        }
      >
        <p>Are you sure you want to delete this User ?</p>
      </Modal>
      <Modal
        onDismiss={() => {
          setVisible(false);
          clearFormState();
        }}
        visible={visible}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !adminNIK ||
                  !adminName ||
                  !adminEmail ||
                  !adminPosition ||
                  !adminTenant ||
                  !adminUsername ||
                  !adminPassword ||
                  !adminAccessrole
                }
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Add User'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='NIK'>
              <Input
                value={adminNIK}
                onChange={({ detail }) => setadminNIK(detail.value)}
              />
            </FormField>
            <FormField label='Name'>
              <Input
                value={adminName}
                onChange={({ detail }) => setadminName(detail.value)}
              />
            </FormField>
            <FormField label='Email'>
              <Input
                value={adminEmail}
                onChange={({ detail }) => setadminEmail(detail.value)}
              />
            </FormField>
            <FormField label='Position'>
              <Input
                value={adminPosition}
                onChange={({ detail }) => setadminPosition(detail.value)}
              />
            </FormField>
            <FormField label='Tenant'>
              <Input
                value={adminTenant}
                onChange={({ detail }) => setadminTenant(detail.value)}
              />
            </FormField>
            <FormField label='Username'>
              <Input
                value={adminUsername}
                onChange={({ detail }) => setadminUsername(detail.value)}
              />
            </FormField>
            <FormField label='Password'>
              <Input
                value={adminPassword}
                onChange={({ detail }) => setadminPassword(detail.value)}
              />
            </FormField>
            <FormField label='Access Role'>
              <Input
                value={adminAccessrole}
                onChange={({ detail }) => setadminAccessrole(detail.value)}
              />
              {/*<Select
                options={[
                  { label: "Admin", value: "1" },
                  { label: "User", value: "2" },
                  { label: "Operator", value: "3" },
                ]}
                selectedOption={null}
              />*/}
            </FormField>

            {/*} <Checkbox
              onChange={({ detail }) => setChecked(detail.checked)}
              checked={checked}
            >
              Active
            </Checkbox>*/}
          </SpaceBetween>
        </Container>
      </Modal>

      <Modal
        onDismiss={() => {
          setVisible2(false);
          clearFormState();
        }}
        visible={visible2}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible2(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSubmit}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Edit User'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='NIK'>
              <Input
                value={adminNIK}
                onChange={({ detail }) => setadminNIK(detail.value)}
              />
            </FormField>
            <FormField label='Name'>
              <Input
                value={adminName}
                onChange={({ detail }) => setadminName(detail.value)}
              />
            </FormField>
            <FormField label='Email'>
              <Input
                value={adminEmail}
                onChange={({ detail }) => setadminEmail(detail.value)}
              />
            </FormField>
            <FormField label='Position'>
              <Input
                value={adminPosition}
                onChange={({ detail }) => setadminPosition(detail.value)}
              />
            </FormField>
            <FormField label='Tenant'>
              <Input
                value={adminTenant}
                onChange={({ detail }) => setadminTenant(detail.value)}
              />
            </FormField>
            <FormField label='Username'>
              <Input
                value={adminUsername}
                onChange={({ detail }) => setadminUsername(detail.value)}
              />
            </FormField>
            <FormField label='Password'>
              <Input
                value={adminPassword}
                onChange={({ detail }) => setadminPassword(detail.value)}
              />
            </FormField>
            <FormField label='Access Role'>
              <Input
                value={adminAccessrole}
                onChange={({ detail }) => setadminAccessrole(detail.value)}
              />
              {/*<Select
                options={[
                  { label: "Admin", value: "1" },
                  { label: "User", value: "2" },
                  { label: "Operator", value: "3" },
                ]}
                selectedOption={null}
              />*/}
            </FormField>

            {/*} <Checkbox
              onChange={({ detail }) => setChecked(detail.checked)}
              checked={checked}
            >
              Active
            </Checkbox>*/}
          </SpaceBetween>
        </Container>
      </Modal>
      <Flashbar items={itemsFlashbar} />

      <Table<ListUser>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master User table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              ListUser,
              collectionProps.selectedItems,
              items.length
            )}
            actions={
              <SpaceBetween size='s' direction='horizontal'>
                {/* <Button
                  disabled={collectionProps.selectedItems?.length === 0}
                  onClick={() => setVisible(true)}
                >
                  Edit
                </Button> */}
                <Button
                  disabled={(collectionProps.selectedItems ?? []).length === 0}
                  onClick={handleDeleteCOnfirmation}
                >
                  Delete
                </Button>
                <Button
                  disabled={(collectionProps.selectedItems ?? []).length === 0}
                  onClick={() => {
                    const selectedItem = collectionProps.selectedItems?.[0];
                    if (selectedItem) {
                      setadminNIK(selectedItem.NIK);
                      setadminName(selectedItem.Name);
                      setadminEmail(selectedItem.Email);
                      setadminPosition(selectedItem.Position);
                      setadminTenant(selectedItem.tenant);
                      setadminUsername(selectedItem.Username);
                      setadminPassword(selectedItem.Password);
                      setadminAccessrole(selectedItem.AccessRole);
                      setselectedAdminId(selectedItem.id);
                      setVisible2(true);
                    }
                  }}
                  variant='primary'
                >
                  Update
                </Button>
                <Button onClick={() => setVisible(true)} variant='primary'>
                  Add User
                </Button>
              </SpaceBetween>
            }
          >
            Users
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find User'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        selectionType='single'
      />
    </>
  );
}
